import React from 'react';

import { SelectOptionsList } from '@/components/SelectOptionsList';
import {
	BottomCard,
	BottomCardAction,
	BottomCardForm,
} from '@/components/shared/BottomCard/BottomCard';
import { AsaphButton } from '@/components/shared/Button';
import { UserMilestones } from '@/config/constants';
import {
	useAddSongsToMyTeamMutation,
	useGetSongColletionMutation,
} from '@/graphql';
import { useErrorHandler } from '@/hooks/useErrorHandler';
import { useAnalyticsEvent } from '@/providers/AnalyticsProvider';
import { useAuth } from '@/providers/AuthProvider';
import { useTeamSongs } from '@/providers/TeamSongsProvider';
import { useSnackbar } from 'notistack';

import { ILayoutProps } from '../../Layout';
import styles from './QuickAddSongsBottomPanel.module.scss';

export interface QuickAddSongsBottomPanelProps {
	onClose: () => void;
	openAddSongs: ILayoutProps['openAddSongs'];
	openImportCsv: () => void;
}

export const QuickAddSongsBottomPanel: React.FC<
	QuickAddSongsBottomPanelProps
> = ({ onClose, openAddSongs, openImportCsv }) => {
	const { handleError } = useErrorHandler();
	const { teamSongs, refetch } = useTeamSongs();
	const { hasMilestones, appendMilestone } = useAuth();
	const { enqueueSnackbar } = useSnackbar();

	const fireSelectItem = useAnalyticsEvent(
		'Quick_Add_Songs_Bottom_Panel_Select',
	);
	const fireAdd = useAnalyticsEvent('Quick_Add_Songs_Bottom_Panel_Add');
	const fireClose = useAnalyticsEvent('Quick_Add_Songs_Bottom_Panel_Close');
	const fireCsvSelect = useAnalyticsEvent(
		'Quick_Add_Songs_Bottom_Panel_CSV_Select',
	);

	const { mutateAsync } = useGetSongColletionMutation();
	const { mutateAsync: addSongsToMyTeam, isLoading: submitting } =
		useAddSongsToMyTeamMutation();

	const [selectedOption, setSelectedOption] = React.useState<
		string | number | null
	>('asaphTop10');

	const quickAddHandler = async () => {
		fireAdd({
			option: selectedOption?.toString() || 'browseAllSongs',
		});

		if (typeof selectedOption !== 'string') {
			openAddSongs({
				callbackFn: (addedSongIds) => {
					if (addedSongIds.length > 0) {
						onClose();
					}
				},
			});
			return;
		}

		if (selectedOption === 'csv') {
			openImportCsv();
			onClose();
			return;
		}

		try {
			const data = await mutateAsync({
				collectionId: selectedOption,
			});
			const songIds = data.getSongCollection.songIds.filter(
				(id) => !teamSongs.find((ts) => ts.originSongId === id),
			);

			await addSongsToMyTeam({ songIds });
			enqueueSnackbar('Songs added to the songlist', {
				variant: 'success',
			});
			refetch();
			onClose();
		} catch (error) {
			handleError({
				error,
				errorCode: 'QuickAddSongsBottomPanelError',
				friendlyMessage:
					'There was an error adding songs from the collection.',
			});
		}
	};

	let ctaLabel = 'Quick Add';
	if (selectedOption === 'csv') {
		ctaLabel = 'Continue';
	}
	if (selectedOption === null) {
		ctaLabel = 'Continue';
	}

	return (
		<BottomCard
			className={styles['root']}
			title="Add Songs"
			hasCloseButton={true}
			onClose={() => {
				onClose();
				fireClose();
			}}
			closeButtonLabel="Close"
		>
			<BottomCardForm>
				<SelectOptionsList
					value={selectedOption}
					onChange={(value) => {
						setSelectedOption(value);
						if (value === 'csv') {
							fireCsvSelect();
						}
					}}
					items={[
						{
							title: 'Asaph Top 20',
							subtitle: 'Top rated across our network',
							value: 'asaphTop10',
						},
						{
							title: 'CCLI Top 20',
							subtitle: 'Industry chart toppers',
							value: 'ccliTop10',
						},
						{
							title: 'Import from CSV',
							subtitle: 'Already have a list? Import it',
							value: 'csv',
						},
						{
							title: 'Browse all songs',
							value: null,
						},
					]}
				/>
			</BottomCardForm>
			<BottomCardAction>
				<AsaphButton
					color="blue"
					fullWidth
					label={ctaLabel}
					onClick={quickAddHandler}
				/>
			</BottomCardAction>
		</BottomCard>
	);
};
